import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, User, Calendar, CheckCircle, Loader2, Globe } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getBrowserTimezone, getTimezoneAbbr, formatTime12WithTz } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CourseCatalog = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=language, 2=course, 3=tutor+time, 4=confirm
  const [languages, setLanguages] = useState([]);
  const [courses, setCourses] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  // Selections
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { day, time }

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const response = await axios.get(`${API}/languages`);
        setLanguages(response.data);
      } catch (error) {
        toast.error("Failed to load languages");
      } finally {
        setLoading(false);
      }
    };
    loadLanguages();
  }, []);

  const selectLanguage = async (lang) => {
    setSelectedLanguage(lang);
    setLoading(true);
    try {
      const viewerTz = getBrowserTimezone();
      const [coursesRes, tutorsRes] = await Promise.all([
        axios.get(`${API}/courses?language_id=${lang.id}`),
        axios.get(`${API}/booking/tutors?language_id=${lang.id}&viewer_tz=${encodeURIComponent(viewerTz)}`)
      ]);
      setCourses(coursesRes.data);
      setTutors(tutorsRes.data);
      setStep(2);
    } catch (error) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const selectCourse = (course) => {
    setSelectedCourse(course);
    setStep(3);
  };

  const selectTutorSlot = (tutor, day, time) => {
    setSelectedTutor(tutor);
    setSelectedSlot({ day, time });
    setStep(4);
  };

  const confirmBooking = async () => {
    setBooking(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(`${API}/booking/enroll`, {
        course_id: selectedCourse.id,
        tutor_id: selectedTutor.id,
        scheduled_day: selectedSlot.day,
        scheduled_time: selectedSlot.time,
        timezone: getBrowserTimezone()
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.has_holiday_skips) {
        const reasons = res.data.holiday_skip_reasons.filter(Boolean).join(", ") || "tutor holiday";
        toast.success(
          `Booked! Note: ${res.data.holiday_skip_count} session(s) moved due to tutor holidays (${reasons}). All 8 sessions are still included.`,
          { duration: 7000 }
        );
      } else {
        toast.success("Booked successfully!");
      }
      navigate("/dashboard");
    } catch (error) {
      const msg = error.response?.data?.detail || "Booking failed";
      toast.error(msg);
    } finally {
      setBooking(false);
    }
  };

  /**
   * Detect whether the selected day/course would create any holiday conflicts
   * for the tutor, so we can show a pre-booking notice in step 4.
   */
  const getHolidayConflicts = () => {
    if (!selectedTutor || !selectedSlot || !selectedCourse) return [];
    const timeOffSet = new Map((selectedTutor.time_off || []).map(t =>
      typeof t === "string" ? [t, ""] : [t.date, t.reason || ""]
    ));
    const today = new Date();
    const jsDay = ((selectedSlot.day + 1) % 7); // backend Mon=0 → JS Sun=0
    const dayDiff = (jsDay - today.getDay() + 7) % 7 || 7;
    const first = new Date(today);
    first.setDate(today.getDate() + dayDiff);
    const conflicts = [];
    const duration = selectedCourse.duration_weeks || 8;
    for (let w = 0; w < duration * 4 && conflicts.length < duration; w++) {
      const d = new Date(first);
      d.setDate(first.getDate() + w * 7);
      const ds = d.toISOString().slice(0, 10);
      if (timeOffSet.has(ds)) conflicts.push({ date: ds, reason: timeOffSet.get(ds) });
    }
    return conflicts;
  };

  const goBack = () => {
    if (step === 2) { setStep(1); setSelectedLanguage(null); }
    else if (step === 3) { setStep(2); setSelectedCourse(null); }
    else if (step === 4) { setStep(3); setSelectedTutor(null); setSelectedSlot(null); }
  };

  const tzAbbr = getTimezoneAbbr();

  const formatTime = (t) => formatTime12WithTz(t, tzAbbr);

  // Generate 1-hour time slots from a range
  const generateSlots = (startTime, endTime) => {
    const slots = [];
    let [sh, sm] = startTime.split(":").map(Number);
    const [eh] = endTime.split(":").map(Number);
    while (sh < eh) {
      slots.push(`${String(sh).padStart(2, "0")}:${String(sm).padStart(2, "0")}`);
      sh += 1;
    }
    return slots;
  };

  /**
   * Compute the 8 session dates for a given day-of-week slot.
   * dayOfWeek: 0=Monday…6=Sunday (Python/backend convention).
   * Returns array of "YYYY-MM-DD" strings.
   */
  const computeSessionDates = (dayOfWeek, numWeeks = 8) => {
    // Convert Python weekday (Mon=0) to JS getDay() (Sun=0, Mon=1)
    const jsDayOfWeek = (dayOfWeek + 1) % 7;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let daysAhead = jsDayOfWeek - today.getDay();
    if (daysAhead <= 0) daysAhead += 7; // always at least 1 day ahead
    const first = new Date(today);
    first.setDate(today.getDate() + daysAhead);
    const dates = [];
    for (let i = 0; i < numWeeks; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i * 7);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      dates.push(`${y}-${m}-${day}`);
    }
    return dates;
  };

  /** Returns conflicting dates (if any) between this day slot and the tutor's time_off. */
  const getConflictDates = (dayOfWeek, timeOffList, numWeeks) => {
    if (!timeOffList || timeOffList.length === 0) return [];
    const offSet = new Set(timeOffList);
    return computeSessionDates(dayOfWeek, numWeeks).filter(d => offSet.has(d));
  };

  if (loading) return (
    <div className="flex justify-center py-12"><div className="spinner"></div></div>
  );

  // Step indicators
  const steps = ["Language", "Course", "Tutor & Time", "Confirm"];

  return (
    <div>
      {/* Back to Dashboard */}
      <Link to="/dashboard" className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A] mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-5 h-5" /> Back to Dashboard
      </Link>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center text-sm font-bold ${
              i + 1 < step ? 'bg-[#D6E6FA]' : i + 1 === step ? 'bg-[#FBE7C6]' : 'bg-[#F0EFEB]'
            }`}>
              {i + 1 < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i + 1 === step ? 'text-[#0A0A0A]' : 'text-[#4A4A4A]'}`}>{s}</span>
            {i < steps.length - 1 && <div className="w-8 h-0.5 bg-[#0A0A0A]/20"></div>}
          </div>
        ))}
      </div>

      {step > 1 && (
        <button onClick={goBack} className="flex items-center gap-1 text-[#4A4A4A] hover:text-[#0A0A0A] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}

      {/* Step 1: Choose Language */}
      {step === 1 && (
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="booking-title">Choose a Language</h1>
          <p className="text-[#4A4A4A] mb-8">What language would you like to learn?</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {languages.map(lang => (
              <button
                key={lang.id}
                onClick={() => selectLanguage(lang)}
                className="brutalist-card p-8 text-center hover:bg-[#D6E6FA]/20 transition-colors group"
                data-testid={`lang-${lang.code}`}
              >
                <span className="text-5xl block mb-3">{lang.flag_emoji}</span>
                <span className="font-bold text-lg group-hover:underline">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Choose Course */}
      {step === 2 && (
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="course-select-title">
            {selectedLanguage?.flag_emoji} Choose a Course
          </h1>
          <p className="text-[#4A4A4A] mb-8">{selectedLanguage?.name} courses available</p>

          {courses.length === 0 ? (
            <div className="brutalist-card p-12 text-center">
              <p className="text-[#4A4A4A]">No courses available for {selectedLanguage?.name} yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {courses.map(course => (
                <button
                  key={course.id}
                  onClick={() => selectCourse(course)}
                  className="brutalist-card p-6 text-left hover:bg-[#FBE7C6]/20 transition-colors group"
                  data-testid={`course-${course.id}`}
                >
                  <h3 className="font-bold text-xl mb-2 group-hover:underline">{course.name}</h3>
                  {course.description && <p className="text-sm text-[#4A4A4A] mb-3">{course.description}</p>}
                  <div className="flex gap-3 text-sm">
                    <span className="px-2 py-0.5 rounded-full bg-[#D6E6FA] border border-[#0A0A0A] font-bold">{course.level}</span>
                    <span className="text-[#4A4A4A]">{course.duration_weeks} weeks</span>
                    <span className="text-[#4A4A4A]">{course.hours_per_class}h/class</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Choose Tutor & Time */}
      {step === 3 && (
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="tutor-select-title">Choose a Tutor & Time</h1>
          <p className="text-[#4A4A4A] mb-2">Pick a tutor and an available time slot for {selectedCourse?.name}</p>
          <p className="text-xs text-[#3C8DE4] font-medium flex items-center gap-1 mb-8" data-testid="timezone-indicator">
            <Globe className="w-3 h-3" /> Times shown in your timezone ({tzAbbr})
          </p>

          {tutors.length === 0 ? (
            <div className="brutalist-card p-12 text-center">
              <p className="text-[#4A4A4A]">No tutors available for {selectedLanguage?.name} right now. Please check back later.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {tutors.map(tutor => (
                <div key={tutor.id} className="brutalist-card p-6" data-testid={`booking-tutor-${tutor.id}`}>
                  <div className="flex items-start gap-4 mb-4">
                    {tutor.photo_url ? (
                      <img src={tutor.photo_url} alt={tutor.name} className="w-20 h-20 rounded-full border-2 border-[#0A0A0A] object-cover" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center text-2xl font-bold">
                        {tutor.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-xl">{tutor.name}</h3>
                      <p className="text-sm text-[#4A4A4A] mt-1 max-w-lg">{tutor.bio}</p>
                      {tutor.spots_remaining != null && (
                        <span className={`inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                          tutor.spots_remaining <= 2
                            ? "bg-[#FFAEBC] border-red-400 text-red-800"
                            : "bg-[#B4F8C8] border-green-400 text-green-800"
                        }`} data-testid={`spots-remaining-${tutor.id}`}>
                          {tutor.spots_remaining} {tutor.spots_remaining === 1 ? "spot" : "spots"} remaining
                        </span>
                      )}
                    </div>
                  </div>

                  <h4 className="font-bold text-sm uppercase tracking-wider text-[#4A4A4A] mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Available Times
                  </h4>

                  <div className="space-y-3">
                    {tutor.availability.map((avail, i) => {
                      const timeSlots = generateSlots(avail.start_time, avail.end_time);
                      const numWeeks = selectedCourse?.duration_weeks || 8;
                      const conflictDates = getConflictDates(avail.day_of_week, tutor.time_off, numWeeks);
                      const dayHasConflict = conflictDates.length > 0;
                      return (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-24 pt-1 font-bold text-sm">{DAY_NAMES[avail.day_of_week]}</div>
                          <div className="flex-1">
                            <div className="flex flex-wrap gap-2">
                              {timeSlots.map(time => {
                                const isBooked = tutor.booked_slots.some(
                                  b => b.day === avail.day_of_week && b.time === time
                                );
                                const isDisabled = isBooked || dayHasConflict;
                                return (
                                  <button
                                    key={`${avail.day_of_week}-${time}`}
                                    onClick={() => !isDisabled && selectTutorSlot(tutor, avail.day_of_week, time)}
                                    disabled={isDisabled}
                                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                                      isBooked
                                        ? 'bg-[#F0EFEB] border-[#0A0A0A]/20 text-[#4A4A4A] line-through cursor-not-allowed'
                                        : dayHasConflict
                                          ? 'bg-[#FFAEBC]/30 border-[#FF6B6B]/40 text-[#4A4A4A] cursor-not-allowed'
                                          : selectedTutor?.id === tutor.id && selectedSlot?.day === avail.day_of_week && selectedSlot?.time === time
                                            ? 'bg-[#D6E6FA] border-[#3C8DE4] text-[#0A0A0A]'
                                            : 'border-[#0A0A0A] bg-white hover:bg-[#D6E6FA] cursor-pointer'
                                    }`}
                                    data-testid={`slot-${tutor.id}-${avail.day_of_week}-${time}`}
                                  >
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {formatTime(time)}
                                    {isBooked && <span className="ml-1 text-xs">(taken)</span>}
                                  </button>
                                );
                              })}
                            </div>
                            {/* Conflict warning shown below the slot row */}
                            {dayHasConflict && (
                              <div className="mt-1 flex items-start gap-1 text-xs text-[#856404]" data-testid={`conflict-warning-${tutor.id}-${avail.day_of_week}`}>
                                <span className="mt-0.5">⚠</span>
                                <span>
                                  Unavailable — tutor is off on{" "}
                                  {conflictDates.slice(0, 3).map(d =>
                                    new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  ).join(", ")}
                                  {conflictDates.length > 3 && ` (+${conflictDates.length - 3} more)`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && selectedTutor && selectedCourse && selectedSlot && (
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="confirm-title">Confirm Your Booking</h1>
          <p className="text-[#4A4A4A] mb-8">Review your selection before confirming</p>

          <div className="brutalist-card p-8 max-w-lg mx-auto" data-testid="booking-summary">
            <div className="space-y-6">
              <div className="flex items-center gap-4 pb-4 border-b border-[#0A0A0A]/10">
                <span className="text-4xl">{selectedLanguage?.flag_emoji}</span>
                <div>
                  <div className="text-sm text-[#4A4A4A]">Course</div>
                  <div className="font-bold text-xl">{selectedCourse.name}</div>
                  <div className="text-sm text-[#4A4A4A]">{selectedCourse.level} - {selectedCourse.duration_weeks} weeks</div>
                </div>
              </div>

              <div className="flex items-center gap-4 pb-4 border-b border-[#0A0A0A]/10">
                {selectedTutor.photo_url ? (
                  <img src={selectedTutor.photo_url} alt="" className="w-14 h-14 rounded-full border-2 border-[#0A0A0A] object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center text-xl font-bold">
                    {selectedTutor.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <div className="text-sm text-[#4A4A4A]">Tutor</div>
                  <div className="font-bold text-lg">{selectedTutor.name}</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#FBE7C6] border-2 border-[#0A0A0A] flex items-center justify-center">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-[#4A4A4A]">Schedule</div>
                  <div className="font-bold text-lg">{DAY_NAMES[selectedSlot.day]}s at {formatTime(selectedSlot.time)}</div>
                  <div className="text-sm text-[#4A4A4A]">Weekly for {selectedCourse.duration_weeks} weeks &middot; {tzAbbr}</div>
                </div>
              </div>

              {/* Holiday disclaimer — shown when the tutor has time-off that overlaps */}
              {(() => {
                const conflicts = getHolidayConflicts();
                if (conflicts.length === 0) return null;
                const reasons = [...new Set(conflicts.map(c => c.reason).filter(Boolean))].join(", ") || "tutor holiday";
                return (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FFF3E0] border border-[#FF9800] text-sm" data-testid="holiday-disclaimer">
                    <span className="text-[#E65100] mt-0.5 shrink-0">&#9888;</span>
                    <span className="text-[#E65100]">
                      <strong>Note:</strong> {conflicts.length} session{conflicts.length > 1 ? "s" : ""} may be moved due to tutor holidays ({reasons}). All {selectedCourse.duration_weeks} sessions are still included.
                    </span>
                  </div>
                );
              })()}
            </div>

            <button
              onClick={confirmBooking}
              disabled={booking}
              className="brutalist-btn-primary w-full mt-8 flex items-center justify-center gap-2"
              data-testid="confirm-booking-btn"
            >
              {booking ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Booking...</>
              ) : (
                <><CheckCircle className="w-5 h-5" /> Confirm Booking</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCatalog;
